import React, { useState, useEffect, useRef } from 'react';
import API_BASE_URL, { API_ENDPOINTS, getCurrentBrand } from '../config/api';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    Slider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    LinearProgress,
    Alert,
    Chip,
    FormControlLabel,
    Stack,
    Tabs,
    Tab,
    ImageList,
    ImageListItem,
    Pagination,
    Divider,
    Switch
} from '@mui/material';
import {
    Save as SaveIcon,
    CloudUpload as UploadIcon,
    Download as DownloadIcon,
    CheckBox as CheckBoxIcon,
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
    SelectAll as SelectAllIcon,
    Deselect as DeselectIcon
} from '@mui/icons-material';

const glassStyles = {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
};

const successGradient = 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)';

const XHSImageCreator = () => {

    // State management
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedImages, setSelectedImages] = useState([]); // For batch selection
    const [batchMode, setBatchMode] = useState(false); // Toggle for batch mode
    const [batchVaryStyles, setBatchVaryStyles] = useState(true); // Use different style per image in batch
    const [xhsImages, setXhsImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [batchGenerating, setBatchGenerating] = useState(false);
    const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
    const [canvasLoading, setCanvasLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Text layers state
    const [textLayers, setTextLayers] = useState([
        {
            id: 'title',
            text: '',
            x: 0.5, // Relative position (0-1)
            y: 0.15,
            fontSize: 64,
            fontFamily: 'Impact',
            color: '#FFFFFF',
            align: 'center',
            shadow: true,
            outline: true,
            outlineColor: '#000000',
            outlineWidth: 6
        },
        {
            id: 'subtitle',
            text: '',
            x: 0.5,
            y: 0.5,
            fontSize: 96,
            fontFamily: 'Impact',
            color: '#FFD700',
            align: 'center',
            shadow: true,
            outline: true,
            outlineColor: '#000000',
            outlineWidth: 8
        },
        {
            id: 'description',
            text: '',
            x: 0.5,
            y: 0.85,
            fontSize: 48,
            fontFamily: 'Arial Black',
            color: '#FFFFFF',
            align: 'center',
            shadow: true,
            outline: true,
            outlineColor: '#000000',
            outlineWidth: 4
        }
    ]);

    const [selectedLayer, setSelectedLayer] = useState(0);
    // eslint-disable-next-line no-unused-vars
    const [templates, setTemplates] = useState({});
    const [styledImageUrl, setStyledImageUrl] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState('yummy');
    const [leftTab, setLeftTab] = useState('templates');
    const [imagePage, setImagePage] = useState(1);
    const previewRef = useRef(null);

    // Template definitions
    const templatePresets = {
        yummy: [
            { id: 'title', text: '', x: 0.5, y: 0.15, fontSize: 64, fontFamily: 'Impact', color: '#FFFFFF', align: 'center', shadow: true, outline: true, outlineColor: '#000000', outlineWidth: 6 },
            { id: 'subtitle', text: '', x: 0.5, y: 0.5, fontSize: 96, fontFamily: 'Impact', color: '#FFD700', align: 'center', shadow: true, outline: true, outlineColor: '#000000', outlineWidth: 8 },
            { id: 'description', text: '', x: 0.5, y: 0.85, fontSize: 48, fontFamily: 'Arial Black', color: '#FFFFFF', align: 'center', shadow: true, outline: true, outlineColor: '#000000', outlineWidth: 4 }
        ],
        sunset: [
            { id: 'title', text: '', x: 0.5, y: 0.2, fontSize: 70, fontFamily: 'Impact', color: '#FF6B6B', align: 'center', shadow: true, outline: true, outlineColor: '#FFA500', outlineWidth: 5 },
            { id: 'subtitle', text: '', x: 0.5, y: 0.5, fontSize: 90, fontFamily: 'Impact', color: '#FFD93D', align: 'center', shadow: true, outline: true, outlineColor: '#FF6B6B', outlineWidth: 7 },
            { id: 'description', text: '', x: 0.5, y: 0.8, fontSize: 44, fontFamily: 'Arial Black', color: '#FFFFFF', align: 'center', shadow: true, outline: true, outlineColor: '#FF8C42', outlineWidth: 3 }
        ],
        ocean: [
            { id: 'title', text: '', x: 0.5, y: 0.25, fontSize: 68, fontFamily: 'Impact', color: '#00D9FF', align: 'center', shadow: true, outline: true, outlineColor: '#0066CC', outlineWidth: 6 },
            { id: 'subtitle', text: '', x: 0.5, y: 0.5, fontSize: 85, fontFamily: 'Impact', color: '#FFFFFF', align: 'center', shadow: true, outline: true, outlineColor: '#00A8CC', outlineWidth: 7 },
            { id: 'description', text: '', x: 0.5, y: 0.75, fontSize: 46, fontFamily: 'Arial Black', color: '#B8F3FF', align: 'center', shadow: true, outline: true, outlineColor: '#005580', outlineWidth: 4 }
        ],
        retro: [
            { id: 'title', text: '', x: 0.5, y: 0.3, fontSize: 76, fontFamily: 'Impact', color: '#FF1493', align: 'center', shadow: true, outline: true, outlineColor: '#FFD700', outlineWidth: 8 },
            { id: 'subtitle', text: '', x: 0.5, y: 0.5, fontSize: 88, fontFamily: 'Impact', color: '#00FFFF', align: 'center', shadow: true, outline: true, outlineColor: '#FF1493', outlineWidth: 7 },
            { id: 'description', text: '', x: 0.5, y: 0.7, fontSize: 50, fontFamily: 'Arial Black', color: '#FFFF00', align: 'center', shadow: true, outline: true, outlineColor: '#FF00FF', outlineWidth: 5 }
        ],
        luxury: [
            { id: 'title', text: '', x: 0.5, y: 0.2, fontSize: 72, fontFamily: 'Impact', color: '#FFD700', align: 'center', shadow: true, outline: true, outlineColor: '#8B4513', outlineWidth: 6 },
            { id: 'subtitle', text: '', x: 0.5, y: 0.5, fontSize: 92, fontFamily: 'Impact', color: '#FFFFFF', align: 'center', shadow: true, outline: true, outlineColor: '#DAA520', outlineWidth: 8 },
            { id: 'description', text: '', x: 0.5, y: 0.78, fontSize: 48, fontFamily: 'Arial Black', color: '#FFF8DC', align: 'center', shadow: true, outline: true, outlineColor: '#B8860B', outlineWidth: 4 }
        ],
        kawaii: [
            { id: 'title', text: '', x: 0.5, y: 0.22, fontSize: 66, fontFamily: 'Arial Black', color: '#FFB6D9', align: 'center', shadow: true, outline: true, outlineColor: '#FFFFFF', outlineWidth: 7 },
            { id: 'subtitle', text: '', x: 0.5, y: 0.5, fontSize: 82, fontFamily: 'Arial Black', color: '#FFFFFF', align: 'center', shadow: true, outline: true, outlineColor: '#FF69B4', outlineWidth: 6 },
            { id: 'description', text: '', x: 0.5, y: 0.75, fontSize: 42, fontFamily: 'Arial', color: '#FFF0F5', align: 'center', shadow: true, outline: true, outlineColor: '#FFB6D9', outlineWidth: 3 }
        ],
        dark: [
            { id: 'title', text: '', x: 0.5, y: 0.25, fontSize: 74, fontFamily: 'Impact', color: '#00FF00', align: 'center', shadow: true, outline: true, outlineColor: '#000000', outlineWidth: 7 },
            { id: 'subtitle', text: '', x: 0.5, y: 0.5, fontSize: 90, fontFamily: 'Impact', color: '#FF0000', align: 'center', shadow: true, outline: true, outlineColor: '#000000', outlineWidth: 8 },
            { id: 'description', text: '', x: 0.5, y: 0.75, fontSize: 48, fontFamily: 'Arial Black', color: '#FFFFFF', align: 'center', shadow: true, outline: true, outlineColor: '#333333', outlineWidth: 5 }
        ],
        gradient: [
            { id: 'title', text: '', x: 0.5, y: 0.2, fontSize: 70, fontFamily: 'Impact', color: '#FF00FF', align: 'center', shadow: true, outline: true, outlineColor: '#FFFFFF', outlineWidth: 6 },
            { id: 'subtitle', text: '', x: 0.5, y: 0.5, fontSize: 88, fontFamily: 'Impact', color: '#00FFFF', align: 'center', shadow: true, outline: true, outlineColor: '#FFFFFF', outlineWidth: 7 },
            { id: 'description', text: '', x: 0.5, y: 0.78, fontSize: 46, fontFamily: 'Arial Black', color: '#FFD700', align: 'center', shadow: true, outline: true, outlineColor: '#FFFFFF', outlineWidth: 4 }
        ]
    };

    // Order of template IDs for cycling when "different style per image" is on
    const batchTemplateIds = ['yummy', 'sunset', 'ocean', 'retro', 'luxury', 'kawaii', 'dark', 'gradient'];

    // Canvas ref for preview
    const canvasRef = useRef(null);
    const imageRef = useRef(null);

    // Fetch XHS images on mount
    useEffect(() => {
        fetchXhsImages();
        fetchTemplates();
    }, []);

    // Redraw canvas when text layers or selected image changes (with debounce)
    useEffect(() => {
        if (batchMode && selectedImages.length > 0) {
            // In batch mode, show preview of first selected image
            setSelectedImage(selectedImages[0].image_url);
            return; // Wait for next effect cycle after setting selectedImage
        }

        if (!selectedImage) return;

        // Debounce canvas redraw to prevent excessive requests
        const timeoutId = setTimeout(() => {
            drawCanvas();
        }, 100); // 100ms debounce

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textLayers, selectedImage, batchMode, selectedImages]);

    const fetchXhsImages = async () => {
        try {
            const brand = getCurrentBrand();
            const response = await fetch(`${API_ENDPOINTS.STORE_IMAGES}?brand=${brand}&limit=50`);
            const data = await response.json();
            setXhsImages(data.images || []);
        } catch (err) {
            console.error('Error fetching XHS images:', err);
        }
    };

    const fetchTemplates = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/xhs-image-templates`);
            const data = await response.json();
            setTemplates(data);
        } catch (err) {
            console.error('Error fetching templates:', err);
        }
    };

    const handleImageSelect = (image) => {
        if (batchMode) {
            // Toggle selection in batch mode
            setSelectedImages(prev => {
                const isSelected = prev.some(img => img.id === image.id);
                if (isSelected) {
                    return prev.filter(img => img.id !== image.id);
                } else {
                    return [...prev, image];
                }
            });
        } else {
            // Single selection mode
            setSelectedImage(image.image_url);
            setError(null);
            setSuccess(null);
            setStyledImageUrl(null);
        }
    };

    const handleSelectAll = () => {
        const currentPageImages = xhsImages.slice(((imagePage || 1) - 1) * 24, (imagePage || 1) * 24);
        setSelectedImages(currentPageImages);
    };

    const handleDeselectAll = () => {
        setSelectedImages([]);
    };

    const isImageSelected = (image) => {
        if (batchMode) {
            return selectedImages.some(img => img.id === image.id);
        }
        return selectedImage === image.image_url;
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setLoading(true);
            setError(null);

            const formData = new FormData();
            formData.append('image', file);

            const brand = getCurrentBrand();
            const response = await fetch(`${API_ENDPOINTS.UPLOAD_TO_R2}?folder=${brand}-xhs&brand=${brand}`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) throw new Error('Upload failed');

            const data = await response.json();
            setSelectedImage(data.url);
            setSuccess('Image uploaded successfully!');
        } catch (err) {
            setError('Failed to upload image: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to get proxy URL for R2 images (fixes CORS issues)
    const getImageUrl = (url) => {
        if (!url) return url;
        // Check if it's an R2 URL (contains r2.dev or R2_PUBLIC_URL pattern)
        const r2Pattern = /r2\.dev|pub-.*\.r2\.dev/i;
        if (r2Pattern.test(url)) {
            // Use proxy endpoint to avoid CORS issues
            return `${API_BASE_URL}/api/proxy-image?url=${encodeURIComponent(url)}`;
        }
        return url;
    };

    const drawCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas || !selectedImage) return;

        setCanvasLoading(true);
        const ctx = canvas.getContext('2d');
        const img = new Image();
        // No need for crossOrigin when using proxy (same-origin)
        // img.crossOrigin = 'anonymous';

        img.onload = () => {
            // Fit image into preview box while preserving aspect ratio
            const box = previewRef.current;
            const maxW = box ? box.clientWidth : 800;
            const maxH = box ? box.clientHeight : 500;
            const scale = Math.min(maxW / img.width, maxH / img.height);
            const targetW = Math.max(1, Math.floor(img.width * scale));
            const targetH = Math.max(1, Math.floor(img.height * scale));

            canvas.width = targetW;
            canvas.height = targetH;

            // Draw image
            ctx.drawImage(img, 0, 0, targetW, targetH);

            // Draw text layers
            textLayers.forEach(layer => {
                if (!layer.text) return;

                const x = layer.x * targetW;
                const y = layer.y * targetH;

                ctx.font = `bold ${layer.fontSize}px ${layer.fontFamily}`;
                ctx.textAlign = layer.align;
                ctx.textBaseline = 'middle';

                // Draw shadow
                if (layer.shadow) {
                    ctx.shadowColor = '#000000';
                    ctx.shadowBlur = 10;
                    ctx.shadowOffsetX = 2;
                    ctx.shadowOffsetY = 2;
                }

                // Draw outline
                if (layer.outline) {
                    ctx.strokeStyle = layer.outlineColor;
                    ctx.lineWidth = layer.outlineWidth;
                    ctx.lineJoin = 'round';
                    ctx.strokeText(layer.text, x, y);
                }

                // Reset shadow
                ctx.shadowColor = 'transparent';
                ctx.shadowBlur = 0;
                ctx.shadowOffsetX = 0;
                ctx.shadowOffsetY = 0;

                // Draw fill
                ctx.fillStyle = layer.color;
                ctx.fillText(layer.text, x, y);
            });

            setCanvasLoading(false);
        };

        img.onerror = () => {
            setCanvasLoading(false);
            setError('Failed to load image');
        };

        // Use proxy URL for R2 images to avoid CORS issues
        img.src = getImageUrl(selectedImage);
        imageRef.current = img;
    };

    const handleGenerateImage = async () => {
        if (!selectedImage) {
            setError('Please select an image first');
            return;
        }

        if (!textLayers.some(layer => layer.text)) {
            setError('Please add some text first');
            return;
        }

        try {
            setGenerating(true);
            setError(null);

            const brand = getCurrentBrand();

            // Convert relative positions to absolute for backend
            const absoluteLayers = textLayers.map(layer => ({
                ...layer,
                // Backend will handle positioning
            }));

            const response = await fetch(`${API_BASE_URL}/api/generate-xhs-styled-image?brand=${brand}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    imageUrl: selectedImage,
                    textLayers: absoluteLayers
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Generation failed');
            }

            const data = await response.json();
            setStyledImageUrl(data.url);
            setSuccess('Styled image generated and saved to XHS library! ✅');

            // Refresh the XHS images list to show the new image
            fetchXhsImages();
        } catch (err) {
            setError('Failed to generate image: ' + err.message);
        } finally {
            setGenerating(false);
        }
    };

    const handleBatchGenerate = async () => {
        if (selectedImages.length === 0) {
            setError('Please select at least one image');
            return;
        }

        if (!textLayers.some(layer => layer.text)) {
            setError('Please add some text first');
            return;
        }

        try {
            setBatchGenerating(true);
            setError(null);
            setSuccess(null);

            const brand = getCurrentBrand();
            const total = selectedImages.length;
            let successCount = 0;
            let failCount = 0;

            setBatchProgress({ current: 0, total });

            // Process images sequentially to avoid overwhelming the server
            for (let i = 0; i < selectedImages.length; i++) {
                const image = selectedImages[i];
                setBatchProgress({ current: i + 1, total });

                // When "different style per image" is on, cycle through templates (same wording, different style)
                let layersToSend;
                if (batchVaryStyles && batchTemplateIds.length > 0) {
                    const templateId = batchTemplateIds[i % batchTemplateIds.length];
                    const styleLayers = templatePresets[templateId];
                    if (styleLayers) {
                        layersToSend = styleLayers.map((styleLayer, idx) => ({
                            ...styleLayer,
                            text: (textLayers[idx] && textLayers[idx].text) ? textLayers[idx].text : styleLayer.text
                        }));
                    } else {
                        layersToSend = textLayers.map(layer => ({ ...layer }));
                    }
                } else {
                    layersToSend = textLayers.map(layer => ({ ...layer }));
                }

                try {
                    const response = await fetch(`${API_BASE_URL}/api/generate-xhs-styled-image?brand=${brand}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            imageUrl: image.image_url,
                            textLayers: layersToSend
                        })
                    });

                    if (response.ok) {
                        successCount++;
                    } else {
                        failCount++;
                        console.error(`Failed to generate image for ${image.image_url}`);
                    }
                } catch (err) {
                    failCount++;
                    console.error(`Error generating image for ${image.image_url}:`, err);
                }

                // Small delay to avoid overwhelming the server
                if (i < selectedImages.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }

            // Show results
            if (failCount === 0) {
                setSuccess(`✅ Successfully generated ${successCount} styled image(s)!`);
            } else {
                setSuccess(`✅ Generated ${successCount} image(s), ${failCount} failed.`);
            }

            // Refresh the XHS images list
            fetchXhsImages();

            // Clear selection after successful batch
            if (failCount === 0) {
                setSelectedImages([]);
            }
        } catch (err) {
            setError('Failed to batch generate images: ' + err.message);
        } finally {
            setBatchGenerating(false);
            setBatchProgress({ current: 0, total: 0 });
        }
    };

    const handleDownload = () => {
        if (!styledImageUrl) return;

        const link = document.createElement('a');
        link.href = styledImageUrl;
        link.download = `xhs-styled-${Date.now()}.png`;
        link.click();
    };

    const updateLayer = (index, updates) => {
        setTextLayers(prev => prev.map((layer, i) =>
            i === index ? { ...layer, ...updates } : layer
        ));
    };

    const applyTemplate = (templateId) => {
        setSelectedTemplate(templateId);
        const template = templatePresets[templateId];

        // Preserve existing text when switching templates
        setTextLayers(prev => template.map((layer, index) => ({
            ...layer,
            text: prev[index]?.text || layer.text
        })));

        setSuccess(`Template "${templateId}" applied! ✨`);
    };

    const currentLayer = textLayers[selectedLayer];
    const hasSelectedImage = batchMode ? selectedImages.length > 0 : Boolean(selectedImage);
    const hasText = textLayers.some(layer => layer.text && layer.text.trim().length > 0);

    return (
        <Box sx={{
            p: { xs: 2, sm: 4 },
            minHeight: '100vh',
            background: '#f8fafc',
            backgroundImage: 'radial-gradient(circle at 20% 10%, rgba(37, 99, 235, 0.08), transparent 45%), radial-gradient(circle at 80% 0%, rgba(16, 185, 129, 0.08), transparent 45%)',
            color: '#0f172a'
        }}>
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h4" sx={{
                        fontWeight: 800,
                        color: '#0f172a',
                        letterSpacing: '-0.02em',
                        mb: 0.75
                    }}>
                        Image Creator Studio
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748b', maxWidth: 520 }}>
                        Design XHS-ready visuals with guided templates, AI suggestions, and precise typography controls.
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Chip label="Studio" sx={{ fontWeight: 700, borderRadius: 2, bgcolor: '#eef2ff', color: '#4338ca' }} />
                    <Chip label="Auto-save to library" sx={{ fontWeight: 600, borderRadius: 2, bgcolor: '#ecfdf3', color: '#047857' }} />
                </Stack>
            </Box>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                    {[
                        {
                            title: '1. Pick a Template',
                            description: 'Apply a style preset to start',
                            status: selectedTemplate ? 'Applied' : 'Pending'
                        },
                        {
                            title: '2. Select Image',
                            description: batchMode ? 'Pick one or more images' : 'Choose a base image',
                            status: hasSelectedImage ? 'Ready' : 'Pending'
                        },
                        {
                            title: '3. Customize Text',
                            description: 'Edit layers, fonts, and colors',
                            status: hasText ? 'Updated' : 'Optional'
                        },
                        {
                            title: '4. Export',
                            description: 'Save the final design',
                            status: hasSelectedImage ? 'Available' : 'Locked'
                        }
                    ].map((step) => (
                        <Grid item xs={12} sm={6} md={3} key={step.title}>
                            <Card
                                sx={{
                                    p: 2,
                                    borderRadius: 4,
                                    border: '1px solid #e2e8f0',
                                    bgcolor: '#ffffff',
                                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
                                    height: '100%'
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
                                    {step.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                                    {step.description}
                                </Typography>
                                <Chip
                                    label={step.status}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        bgcolor: step.status === 'Ready' || step.status === 'Applied' || step.status === 'Updated' || step.status === 'Available'
                                            ? '#dcfce7'
                                            : step.status === 'Optional'
                                                ? '#e2e8f0'
                                                : '#fee2e2',
                                        color: step.status === 'Ready' || step.status === 'Applied' || step.status === 'Updated' || step.status === 'Available'
                                            ? '#166534'
                                            : step.status === 'Optional'
                                                ? '#475569'
                                                : '#991b1b'
                                    }}
                                />
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="stretch">
                {/* Left Panel - Templates / Images Tabs */}
                <Grid item xs={12} md={3}>
                    <Card sx={{
                        ...glassStyles,
                        borderRadius: 6,
                        height: { xs: 'auto', md: 'calc(100vh - 180px)' },
                        minHeight: { xs: 400, md: 'auto' },
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
                            <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
                                <Tabs
                                    value={leftTab}
                                    onChange={(e, val) => setLeftTab(val)}
                                    variant="fullWidth"
                                    sx={{
                                        minHeight: 40,
                                        bgcolor: '#f8fafc',
                                        borderRadius: 3,
                                        p: 0.5,
                                        '& .MuiTabs-indicator': { display: 'none' },
                                        '& .MuiTab-root': {
                                            minHeight: 36,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            color: '#64748b',
                                            borderRadius: 2.5,
                                            transition: 'all 0.2s ease',
                                            '&.Mui-selected': {
                                                color: '#0f172a',
                                                bgcolor: '#ffffff',
                                                boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)'
                                            }
                                        }
                                    }}
                                >
                                    <Tab label="Templates" value="templates" />
                                    <Tab label="Images" value="images" />
                                </Tabs>
                            </Box>

                            {leftTab === 'templates' ? (
                                <Box sx={{ flex: 1, minHeight: 0, p: 2, overflowY: 'auto' }}>
                                    <Box sx={{ mb: 1 }} />

                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#475569' }}>
                                        Premium Styles
                                    </Typography>
                                    <Grid container spacing={2} sx={{ mb: 4 }}>
                                        {[
                                            { id: 'luxury', name: '👑 Luxury Gold', description: 'Elite aesthetics', colors: ['#FFD700', '#B8860B'], preview: 'VIP' },
                                            { id: 'kawaii', name: '🌸 Kawaii Pink', description: 'Soft & cute', colors: ['#FFB6D9', '#FF69B4'], preview: 'CUTE' }
                                        ].map((template) => (
                                            <Grid item xs={6} key={template.id}>
                                                <Card
                                                    sx={{
                                                        cursor: 'pointer',
                                                        borderRadius: 4,
                                                        overflow: 'hidden',
                                                        border: selectedTemplate === template.id ? '2px solid #2563eb' : '1px solid #e2e8f0',
                                                        boxShadow: selectedTemplate === template.id ? '0 10px 24px rgba(37, 99, 235, 0.2)' : 'none',
                                                        transition: 'all 0.2s ease',
                                                        background: '#ffffff',
                                                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12)' }
                                                    }}
                                                    onClick={() => applyTemplate(template.id)}
                                                >
                                                    <Box sx={{ height: 96, background: `linear-gradient(135deg, ${template.colors[0]} 0%, ${template.colors[1]} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Typography sx={{ fontSize: 22, fontWeight: 900, color: '#fff', textShadow: '0 4px 10px rgba(0,0,0,0.2)' }}>{template.preview}</Typography>
                                                    </Box>
                                                    <Box sx={{ p: 1.5, textAlign: 'center' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>{template.name}</Typography>
                                                        <Typography variant="caption" sx={{ color: '#64748b' }}>{template.description}</Typography>
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#475569' }}>
                                        All Templates
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {[
                                            { id: 'yummy', name: '🍜 YUMMY', colors: ['#ff9a9e', '#fecfef'], preview: '必吃' },
                                            { id: 'sunset', name: '🌅 Sunset', colors: ['#f6d365', '#fda085'], preview: 'HOT' },
                                            { id: 'ocean', name: '🌊 Ocean', colors: ['#a1c4fd', '#c2e9fb'], preview: 'FRESH' },
                                            { id: 'retro', name: '🕹️ Retro', colors: ['#84fab0', '#8fd3f4'], preview: 'POP' },
                                            { id: 'dark', name: '🌙 Dark', colors: ['#232526', '#414345'], preview: 'DARK' },
                                            { id: 'gradient', name: '🌈 Rainbow', colors: ['#fa709a', '#fee140'], preview: 'WOW' }
                                        ].map((template) => (
                                            <Grid item xs={6} key={template.id}>
                                                <Card
                                                    sx={{
                                                        cursor: 'pointer',
                                                        borderRadius: 3,
                                                        overflow: 'hidden',
                                                        border: selectedTemplate === template.id ? '2px solid #2563eb' : '1px solid #e2e8f0',
                                                        boxShadow: selectedTemplate === template.id ? '0 8px 20px rgba(37, 99, 235, 0.14)' : 'none',
                                                        transition: 'all 0.2s ease',
                                                        background: '#ffffff',
                                                        '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 10px 20px rgba(15, 23, 42, 0.1)' }
                                                    }}
                                                    onClick={() => applyTemplate(template.id)}
                                                >
                                                    <Box sx={{ height: 74, background: `linear-gradient(135deg, ${template.colors[0]} 0%, ${template.colors[1] || template.colors[0]} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Typography sx={{ fontSize: 18, fontWeight: 900, color: '#fff', textShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>{template.preview}</Typography>
                                                    </Box>
                                                    <Box sx={{ p: 1, textAlign: 'center' }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>{template.name}</Typography>
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            ) : (
                                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 2 }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        fullWidth
                                        startIcon={<UploadIcon />}
                                        sx={{
                                            mb: 3,
                                            borderRadius: 3,
                                            py: 1.4,
                                            border: '1px dashed #cbd5f5',
                                            color: '#1e293b',
                                            bgcolor: '#f8fafc',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            '&:hover': { border: '1px dashed #94a3b8', bgcolor: '#f1f5f9' }
                                        }}
                                        disabled={loading}
                                    >
                                        Upload New Asset
                                        <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                                    </Button>

                                    {/* Batch Mode Toggle */}
                                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: batchMode ? '#eef2ff' : '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={batchMode}
                                                    onChange={(e) => {
                                                        setBatchMode(e.target.checked);
                                                        if (!e.target.checked) {
                                                            setSelectedImages([]);
                                                        }
                                                    }}
                                                />
                                            }
                                            label="Batch Mode"
                                        />
                                        {batchMode && (
                                            <Chip
                                                label={`${selectedImages.length} selected`}
                                                size="small"
                                                sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 700 }}
                                            />
                                        )}
                                    </Box>

                                    {/* Batch: different style per image */}
                                    {batchMode && (
                                        <Box sx={{ mb: 2, p: 1.5, bgcolor: batchVaryStyles ? '#f0fdf4' : '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={batchVaryStyles}
                                                        onChange={(e) => setBatchVaryStyles(e.target.checked)}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                                                        Use different style for each image
                                                    </Typography>
                                                }
                                            />
                                        </Box>
                                    )}

                                    {/* Batch Controls */}
                                    {batchMode && (
                                        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<SelectAllIcon />}
                                                onClick={handleSelectAll}
                                                fullWidth
                                            >
                                                Select All
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<DeselectIcon />}
                                                onClick={handleDeselectAll}
                                                fullWidth
                                            >
                                                Clear
                                            </Button>
                                        </Box>
                                    )}

                                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                                        <ImageList cols={2} gap={8} rowHeight={200} sx={{ m: 0 }}>
                                            {xhsImages.slice(((imagePage || 1) - 1) * 24, (imagePage || 1) * 24).map((img) => (
                                                <ImageListItem key={img.id} sx={{ height: 200 }}>
                                                    <Card
                                                        sx={{
                                                            width: '100%',
                                                            height: 200,
                                                            cursor: 'pointer',
                                                            borderRadius: 3,
                                                            border: isImageSelected(img) ? '2px solid #2563eb' : '1px solid #e2e8f0',
                                                            boxShadow: isImageSelected(img) ? '0 10px 24px rgba(37, 99, 235, 0.18)' : 'none',
                                                            '&:hover': { boxShadow: '0 8px 18px rgba(15, 23, 42, 0.12)' },
                                                            overflow: 'hidden',
                                                            position: 'relative',
                                                            background: '#ffffff'
                                                        }}
                                                        onClick={() => handleImageSelect(img)}
                                                    >
                                                        {batchMode && (
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 8,
                                                                    right: 8,
                                                                    zIndex: 10,
                                                            bgcolor: 'rgba(255, 255, 255, 0.92)',
                                                                    borderRadius: '50%',
                                                                    p: 0.5
                                                                }}
                                                            >
                                                                {isImageSelected(img) ? (
                                                                    <CheckBoxIcon sx={{ color: '#1d4ed8', fontSize: 24 }} />
                                                                ) : (
                                                                    <CheckBoxOutlineBlankIcon sx={{ color: '#666', fontSize: 24 }} />
                                                                )}
                                                            </Box>
                                                        )}
                                                        <Box
                                                            component="img"
                                                            src={getImageUrl(img.image_url)}
                                                            alt="XHS"
                                                            loading="lazy"
                                                            sx={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                backgroundColor: '#f5f7fb',
                                                                display: 'block'
                                                            }}
                                                            onError={(e) => {
                                                                // Fallback to direct URL if proxy fails
                                                                if (e.target.src.includes('/api/proxy-image')) {
                                                                    e.target.src = img.image_url;
                                                                }
                                                            }}
                                                        />
                                                    </Card>
                                                </ImageListItem>
                                            ))}
                                        </ImageList>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1, pt: 1, borderTop: '1px solid #e5e7eb' }}>
                                        <Pagination
                                            count={Math.max(1, Math.ceil(xhsImages.length / 24))}
                                            page={imagePage || 1}
                                            onChange={(e, val) => setImagePage(val)}
                                            size="small"
                                        />
                                    </Box>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Center Panel - Canvas Preview */}
                <Grid item xs={12} md={6}>
                    <Card sx={{
                        ...glassStyles,
                        borderRadius: 6,
                        height: { xs: 'auto', md: 'calc(100vh - 180px)' },
                        minHeight: { xs: 400, md: 'auto' },
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        Canvas Preview
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                        Position text layers and export a ready-to-post design.
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1.5}>
                                    {!batchMode ? (
                                        <Button
                                            variant="contained"
                                            startIcon={generating ? <CircularProgress size={18} thickness={6} color="inherit" /> : <SaveIcon />}
                                            onClick={handleGenerateImage}
                                            disabled={!selectedImage || generating}
                                            sx={{
                                                borderRadius: 3,
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                background: successGradient,
                                                color: '#fff',
                                                '&:hover': { opacity: 0.92 }
                                            }}
                                        >
                                            Export Design
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            startIcon={batchGenerating ? <CircularProgress size={18} thickness={6} color="inherit" /> : <SaveIcon />}
                                            onClick={handleBatchGenerate}
                                            disabled={selectedImages.length === 0 || batchGenerating}
                                            sx={{
                                                borderRadius: 3,
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                background: successGradient,
                                                '&:hover': { opacity: 0.9 }
                                            }}
                                        >
                                            {batchGenerating ? `Processing (${batchProgress.current}/${batchProgress.total})` : `Batch Process (${selectedImages.length})`}
                                        </Button>
                                    )}
                                </Stack>
                            </Box>

                            <Box
                                sx={{
                                    width: '100%',
                                    minHeight: { xs: 300, sm: 400, md: 500 },
                                    flex: 1,
                                    bgcolor: '#f8fafc',
                                    backgroundImage: 'linear-gradient(transparent 23px, rgba(226, 232, 240, 0.7) 24px), linear-gradient(90deg, transparent 23px, rgba(226, 232, 240, 0.7) 24px)',
                                    backgroundSize: '24px 24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 4,
                                    border: selectedImage ? '1px solid #e2e8f0' : '2px dashed #cbd5f5',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    transition: 'all 0.3s ease',
                                    boxShadow: selectedImage ? 'inset 0 2px 4px rgba(15, 23, 42, 0.06)' : 'none'
                                }}
                                ref={previewRef}
                            >
                                {canvasLoading && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                            zIndex: 10
                                        }}
                                    >
                                        <CircularProgress />
                                    </Box>
                                )}
                                {selectedImage ? (
                                    <canvas
                                        ref={canvasRef}
                                        style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <Typography sx={{ color: '#64748b' }}>
                                        Select an image to start
                                    </Typography>
                                )}
                            </Box>

                            {styledImageUrl && !batchMode && (
                                <Box sx={{ mt: 2 }}>
                                    <Alert severity="success" action={
                                        <Button color="inherit" size="small" onClick={handleDownload}>
                                            <DownloadIcon /> Download
                                        </Button>
                                    }>
                                        ✅ Styled image saved to XHS library! You can now use it in your XHS posts.
                                    </Alert>
                                </Box>
                            )}

                            {batchGenerating && (
                                <Box sx={{ mt: 2 }}>
                                    <Alert severity="info">
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            Processing {batchProgress.current} of {batchProgress.total} images...
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={(batchProgress.current / batchProgress.total) * 100}
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                    </Alert>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card sx={{
                        ...glassStyles,
                        borderRadius: 6,
                        height: { xs: 'auto', md: 'calc(100vh - 180px)' },
                        minHeight: { xs: 400, md: 'auto' },
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3, overflowY: 'auto' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#475569' }}>
                                Layer Stack
                            </Typography>

                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel>Edit layer</InputLabel>
                                <Select
                                    value={selectedLayer}
                                    label="Edit layer"
                                    onChange={(e) => setSelectedLayer(Number(e.target.value))}
                                    sx={{ borderRadius: 3, bgcolor: '#f8fafc' }}
                                >
                                    <MenuItem value={0}>Top (Title)</MenuItem>
                                    <MenuItem value={1}>Middle (Subtitle)</MenuItem>
                                    <MenuItem value={2}>Bottom (Description)</MenuItem>
                                </Select>
                            </FormControl>

                            <Divider sx={{ my: 3, opacity: 0.7 }} />

                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 3, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#475569' }}>
                                Layer Configuration
                            </Typography>

                            <TextField
                                fullWidth
                                label="Display Text"
                                variant="outlined"
                                value={currentLayer.text}
                                onChange={(e) => updateLayer(selectedLayer, { text: e.target.value })}
                                sx={{
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        bgcolor: '#f8fafc',
                                    }
                                }}
                            />

                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel>Typography</InputLabel>
                                <Select
                                    value={currentLayer.fontFamily}
                                    label="Typography"
                                    onChange={(e) => updateLayer(selectedLayer, { fontFamily: e.target.value })}
                                    sx={{ borderRadius: 3, bgcolor: '#f8fafc' }}
                                >
                                    <MenuItem value="Impact">Impact (Classic)</MenuItem>
                                    <MenuItem value="Arial Black">Arial Black (Bold)</MenuItem>
                                    <MenuItem value="Arial">Arial (Standard)</MenuItem>
                                    <MenuItem value="Helvetica">Helvetica (Modern)</MenuItem>
                                    <MenuItem value="Comic Sans MS">Comic Sans (Playful)</MenuItem>
                                </Select>
                            </FormControl>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', mb: 1, display: 'block' }}>
                                    Size: {currentLayer.fontSize}px
                                </Typography>
                                <Slider
                                    value={currentLayer.fontSize}
                                    onChange={(e, val) => updateLayer(selectedLayer, { fontSize: val })}
                                    min={20}
                                    max={120}
                                    sx={{ color: '#2563eb' }}
                                />
                            </Box>

                            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b' }}>Fill Color</Typography>
                                <input
                                    type="color"
                                    value={currentLayer.color}
                                    onChange={(e) => updateLayer(selectedLayer, { color: e.target.value })}
                                    style={{ border: 'none', borderRadius: '4px', height: '30px', width: '60px', cursor: 'pointer', background: 'transparent' }}
                                />
                            </Box>

                            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b' }}>Enable Outline</Typography>
                                <Switch
                                    checked={currentLayer.outline}
                                    onChange={(e) => updateLayer(selectedLayer, { outline: e.target.checked })}
                                    color="primary"
                                />
                            </Box>

                            {currentLayer.outline && (
                                <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(99, 102, 241, 0.03)', borderRadius: 3, border: '1px solid rgba(99, 102, 241, 0.1)' }}>
                                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Color</Typography>
                                        <input
                                            type="color"
                                            value={currentLayer.outlineColor}
                                            onChange={(e) => updateLayer(selectedLayer, { outlineColor: e.target.value })}
                                            style={{ border: 'none', borderRadius: '4px', height: '24px', width: '40px', cursor: 'pointer' }}
                                        />
                                    </Box>
                                    <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: 'block' }}>Width: {currentLayer.outlineWidth}px</Typography>
                                    <Slider
                                        value={currentLayer.outlineWidth}
                                        onChange={(e, val) => updateLayer(selectedLayer, { outlineWidth: val })}
                                        min={1}
                                        max={15}
                                        size="small"
                                    />
                                </Box>
                            )}

                            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b' }}>Drop Shadow</Typography>
                                <Switch
                                    checked={currentLayer.shadow}
                                    onChange={(e) => updateLayer(selectedLayer, { shadow: e.target.checked })}
                                    color="primary"
                                />
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', mb: 1, display: 'block' }}>
                                    Vertical Position: {Math.round(currentLayer.y * 100)}%
                                </Typography>
                                <Slider
                                    value={currentLayer.y}
                                    onChange={(e, val) => updateLayer(selectedLayer, { y: val })}
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    sx={{ color: '#2563eb' }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default XHSImageCreator;
